import { useId, type FC, type MouseEvent } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { updateUriRef } from '../../../actions/filterActions.ts';
import { SbSharedRefineMenuInputCheckbox } from '../SbSharedRefineMenuInput/SbSharedRefineMenuInputCheckbox.jsx';
import { useAppliedFilterUrlLabels } from '../../helpers/appliedFilters/useAppliedFilterUrlLabel.ts';

import { type SbSharedRefineMenuMultiSelect_itemSearch$key } from './__generated__/SbSharedRefineMenuMultiSelect_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuMultiSelect_itemSearch on ItemSearchQueryConnection {
        ...useAppliedFilterUrlLabel_itemSearch
    }
`;

export type FilterValue = {
    readonly count: number | null;
    readonly displayName: string | null;
    readonly linkReference: string | null;
    readonly urlLabel: string | null;
    readonly linkable?: boolean | null;
    isHidden?: boolean;
};

type Props = {
    itemSearch: SbSharedRefineMenuMultiSelect_itemSearch$key;
    filterName: string;
    valuesToShow: FilterValue[];
    trackingOptions?: Record<string, unknown>;
};

export const SbSharedRefineMenuMultiSelect: FC<Props> = ({
    itemSearch: itemSearchRef,
    filterName,
    valuesToShow,
    trackingOptions,
}) => {
    const htmlId = useId();
    const dispatch = useDispatch();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const appliedUrlLabels = useAppliedFilterUrlLabels(itemSearch, filterName);

    return (
        <>
            {valuesToShow.map((filterValue, index) => {
                const isChecked = appliedUrlLabels.some(
                    appliedUrlLabel => appliedUrlLabel === filterValue.urlLabel
                );
                return (
                    <SbSharedRefineMenuInputCheckbox
                        key={`${filterValue.urlLabel}-key-${htmlId}-${index}`}
                        checkboxId={`${filterValue.urlLabel}-checkbox-${htmlId}-${index}`}
                        value={filterValue.urlLabel || ''}
                        checked={isChecked}
                        linkReference={filterValue.linkReference}
                        linkable={filterValue.linkable}
                        onChange={(_checked: boolean, event: MouseEvent<HTMLElement>) => {
                            dispatch(
                                updateUriRef({
                                    filterName,
                                    filterValue,
                                    isDeselected: isChecked,
                                    event,
                                    uriRef: undefined,
                                    ga: undefined,
                                    trackingOptions,
                                })
                            );
                        }}
                        text={filterValue.displayName || ''}
                        count={filterValue.count}
                        isHidden={filterValue.isHidden}
                    />
                );
            })}
        </>
    );
};
